import React, { useEffect, useState, useRef } from 'react';
import { Rocket, Plus, Trash2, ToggleLeft, ToggleRight, AlertTriangle, X, Smartphone, Monitor, RefreshCw, Upload, Link, FileArchive } from 'lucide-react';
import { HitApi } from '../../../Api/ApiHit';
import { addOtaUpdate, uploadOtaBundle, searchOtaUpdate, updateOtaUpdate, deleteOtaUpdate } from '../../../constant/Constant';
import { getAcessToken } from '../../../storage/Storage';
import toast from 'react-hot-toast';

const OtaUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [bundleFile, setBundleFile] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    version: '',
    buildNumber: '',
    platform: 'android',
    app: 'partner',
    bundleUrl: '',
    releaseNotes: '',
    forceUpdate: false,
  });

  useEffect(() => { fetchUpdates(); }, []);

  const fetchUpdates = () => {
    setLoading(true);
    HitApi({ page: 1, limit: 50, sortBy: 'buildNumber', sortOrder: 'desc', app: 'partner' }, searchOtaUpdate)
      .then((res) => { if (res?.success) setUpdates(res.data?.docs || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setForm({ version: '', buildNumber: '', platform: 'both', app: 'partner', bundleUrl: '', releaseNotes: '', forceUpdate: false });
    setEditId(null);
    setShowForm(false);
    setBundleFile(null);
    setUploadProgress(0);
  };

  const handleEdit = (item) => {
    setForm({
      version: item.version,
      buildNumber: String(item.buildNumber),
      platform: item.platform,
      app: item.app,
      bundleUrl: item.bundleUrl,
      releaseNotes: item.releaseNotes || '',
      forceUpdate: item.forceUpdate,
    });
    setEditId(item._id);
    setUploadMode('url');
    setShowForm(true);
  };

  const handleFileUploadSave = async () => {
    if (!form.version || !form.buildNumber) {
      toast.error('Version and build number are required');
      return;
    }
    if (!bundleFile) {
      toast.error('Please select a bundle file');
      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('bundle', bundleFile);
      formData.append('version', form.version);
      formData.append('buildNumber', form.buildNumber);
      formData.append('platform', form.platform);
      formData.append('app', form.app);
      formData.append('releaseNotes', form.releaseNotes);
      formData.append('forceUpdate', form.forceUpdate);

      const token = getAcessToken();
      const xhr = new XMLHttpRequest();

      await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.success || result.statusCode === 201) {
              toast.success('Bundle uploaded and OTA update published!');
              resetForm();
              fetchUpdates();
              resolve();
            } else {
              toast.error(result.message || 'Upload failed');
              reject(new Error(result.message));
            }
          } catch (e) {
            toast.error('Upload failed');
            reject(e);
          }
        };
        xhr.onerror = () => { toast.error('Upload failed'); reject(new Error('Upload failed')); };
        xhr.open('POST', uploadOtaBundle);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleUrlSave = () => {
    if (!form.version || !form.buildNumber || !form.bundleUrl) {
      toast.error('Version, build number and bundle URL are required');
      return;
    }
    setSaving(true);
    const payload = { ...form, buildNumber: parseInt(form.buildNumber) };
    if (editId) payload._id = editId;

    HitApi(payload, editId ? updateOtaUpdate : addOtaUpdate)
      .then((res) => {
        if (res?.success || res?.statusCode === 200 || res?.statusCode === 201) {
          toast.success(editId ? 'Update modified' : 'OTA update published');
          resetForm();
          fetchUpdates();
        } else {
          toast.error(res?.message || 'Failed to save');
        }
      })
      .catch(() => toast.error('Failed to save'))
      .finally(() => setSaving(false));
  };

  const handleSave = () => {
    if (uploadMode === 'file' && !editId) {
      handleFileUploadSave();
    } else {
      handleUrlSave();
    }
  };

  const handleDelete = (id) => {
    HitApi({ _id: id }, deleteOtaUpdate)
      .then((res) => { if (res?.success) { toast.success('Deleted'); setDeleteModal(null); fetchUpdates(); } })
      .catch(() => toast.error('Failed to delete'));
  };

  const handleToggleActive = (item) => {
    HitApi({ _id: item._id, isActive: !item.isActive }, updateOtaUpdate)
      .then((res) => { if (res?.success || res?.statusCode === 200) { toast.success(item.isActive ? 'Deactivated' : 'Activated'); fetchUpdates(); } })
      .catch(() => toast.error('Failed to toggle'));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OTA Updates</h1>
          <p className="text-sm text-gray-500 mt-1">Publish over-the-air JS bundle updates</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchUpdates} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800">
            <Plus className="w-4 h-4" /> Publish Update
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{editId ? 'Edit Update' : 'New OTA Update'}</h2>
            <button onClick={resetForm} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input type="text" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="e.g. 1.2.0" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Build Number</label>
              <input type="number" value={form.buildNumber} onChange={(e) => setForm({ ...form, buildNumber: e.target.value })}
                placeholder="e.g. 5" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200">
                <option value="android">Android</option>
                <option value="ios">iOS</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">App</label>
              <input type="text" value="Partner App" disabled className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
            </div>

            {/* Upload Mode Toggle */}
            {!editId && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bundle Source</label>
                <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit">
                  <button onClick={() => setUploadMode('file')}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-md transition-colors ${uploadMode === 'file' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                    <Upload className="w-3.5 h-3.5" /> Upload File
                  </button>
                  <button onClick={() => setUploadMode('url')}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-md transition-colors ${uploadMode === 'url' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                    <Link className="w-3.5 h-3.5" /> Paste URL
                  </button>
                </div>
              </div>
            )}

            {/* File Upload */}
            {uploadMode === 'file' && !editId && (
              <div className="md:col-span-2">
                <input type="file" ref={fileInputRef} accept=".zip,.bundle,.jsbundle,.gz"
                  onChange={(e) => setBundleFile(e.target.files[0])} className="hidden" />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                >
                  {bundleFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileArchive className="w-8 h-8 text-green-600" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">{bundleFile.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(bundleFile.size)}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setBundleFile(null); }}
                        className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Click to upload bundle file</p>
                      <p className="text-xs text-gray-400 mt-1">.zip, .bundle, .jsbundle, .gz (max 50MB)</p>
                    </>
                  )}
                </div>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Uploading...</span>
                      <span className="text-xs font-medium text-gray-700">{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gray-900 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* URL Input */}
            {(uploadMode === 'url' || editId) && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Bundle URL</label>
                <input type="url" value={form.bundleUrl} onChange={(e) => setForm({ ...form, bundleUrl: e.target.value })}
                  placeholder="https://storage.googleapis.com/..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200" />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Release Notes</label>
              <textarea value={form.releaseNotes} onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })}
                rows={2} placeholder="What's new in this update..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 resize-none" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Force Update</label>
              <button onClick={() => setForm({ ...form, forceUpdate: !form.forceUpdate })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.forceUpdate ? 'bg-red-500' : 'bg-gray-300'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.forceUpdate ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              {form.forceUpdate && <span className="text-xs text-red-500 font-medium">Users must update</span>}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-6">
            <button onClick={resetForm} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              <Rocket className={`w-4 h-4 ${saving ? 'animate-bounce' : ''}`} />
              {saving ? (uploadMode === 'file' ? 'Uploading...' : 'Publishing...') : editId ? 'Save Changes' : 'Publish Update'}
            </button>
          </div>
        </div>
      )}

      {/* Updates Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Version</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Build</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Force</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Released</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Notes</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-400">Loading...</td></tr>
              ) : updates.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-gray-400">No OTA updates published yet</td></tr>
              ) : updates.map((item) => (
                <tr key={item._id} className={`hover:bg-gray-50 transition-colors ${!item.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3"><span className="text-sm font-semibold text-gray-900">v{item.version}</span></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">#{item.buildNumber}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {item.platform === 'android' ? <Smartphone className="w-3.5 h-3.5 text-green-600" /> :
                       item.platform === 'ios' ? <Monitor className="w-3.5 h-3.5 text-blue-600" /> :
                       <span className="text-[10px] font-bold text-purple-600">ALL</span>}
                      <span className="text-xs text-gray-600 capitalize">{item.platform}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {item.forceUpdate
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Required</span>
                      : <span className="text-xs text-gray-400">Optional</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs text-gray-500">{new Date(item.releasedAt || item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></td>
                  <td className="px-4 py-3"><span className="text-xs text-gray-500 truncate max-w-[150px] block">{item.releaseNotes || '—'}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg" title="Edit"><Rocket className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleToggleActive(item)} className="p-1.5 hover:bg-gray-100 rounded-lg" title={item.isActive ? 'Deactivate' : 'Activate'}>
                        {item.isActive ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                      </button>
                      <button onClick={() => setDeleteModal(item)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteModal(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete OTA Update</h3>
            <p className="text-sm text-gray-500 mb-4">Delete v{deleteModal.version} (Build #{deleteModal.buildNumber})? The bundle will also be removed from Firebase Storage.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteModal._id)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtaUpdates;
